import { useQuery } from "@tanstack/react-query";
import { Link, useParams } from "react-router";
import { api } from "../api";
import { KIND_LABELS, when } from "../accounts";
import { useRole } from "../components/Shell";
import { errorText, unwrap } from "../problem";

const EVENT_LABELS = { upcoming: "Upcoming", live: "Live", ended: "Ended" } as const;

// Opening a circle is audited (`circle.open`), so it is never refetched by itself.
export function CirclePage() {
  const { circleId = "" } = useParams();
  const role = useRole();
  const circle = useQuery({
    queryKey: ["circle", circleId],
    queryFn: async () => unwrap(await api.GET("/circles/{circleId}", { params: { path: { circleId } } })),
    // Every visit fetches (and is audited) afresh: nothing is kept once the page closes.
    gcTime: 0,
    staleTime: Infinity,
    refetchOnWindowFocus: false,
  });
  if (circle.isPending) return <p>Loading…</p>;
  if (circle.isError) return <p className="error">{errorText(circle.error)}</p>;
  const data = circle.data;
  return (
    <>
      <p>
        <Link to="/circles">← Circles</Link>
      </p>
      <h1>{data.name}</h1>
      <table className="facts">
        <tbody>
          <tr>
            <th>Kind</th>
            <td>{KIND_LABELS[data.kind]}</td>
          </tr>
          <tr>
            <th>Id</th>
            <td>
              <code>{data.id}</code>
            </td>
          </tr>
          <tr>
            <th>Created</th>
            <td>{when(data.created_at)}</td>
          </tr>
          <tr>
            <th>Active invites</th>
            <td>{data.active_invites}</td>
          </tr>
          <tr>
            <th>Places</th>
            <td>{data.places}</td>
          </tr>
          <tr>
            <th>API tokens</th>
            <td>{data.api_tokens}</td>
          </tr>
          {data.event && (
            <tr>
              <th>Event</th>
              <td>
                {EVENT_LABELS[data.event.status]} · {when(data.event.starts_at)} – {when(data.event.ends_at)} (
                {data.event.time_zone})
              </td>
            </tr>
          )}
        </tbody>
      </table>
      {role === "owner" && (
        <p>
          <Link to={`/audit?target_type=circle&target_id=${data.id}`}>Audit log for this circle</Link>
        </p>
      )}
      <section className="stack">
        <h2>Members ({data.members.length})</h2>
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Email</th>
              <th>Role</th>
              <th>Joined</th>
              <th>Notes</th>
            </tr>
          </thead>
          <tbody>
            {data.members.map((member) => (
              <tr key={member.user_id}>
                <td>
                  <Link to={`/users/${member.user_id}`}>{member.name}</Link>
                </td>
                <td>{member.email}</td>
                <td>{member.role === "admin" ? "Admin" : "Member"}</td>
                <td>{when(member.joined_at)}</td>
                <td>
                  {[
                    member.user_id === data.event?.organiser_id ? "Organiser" : null,
                    member.sharing_paused ? "Paused here" : null,
                    member.supervised ? "Supervised" : null,
                    member.suspended ? "Suspended" : null,
                  ]
                    .filter(Boolean)
                    .join(" · ") || "—"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </>
  );
}
